/**
 * Pallet checklists (jobq-api main.py "PALLET HANDOFFS", 2026-10-26): the list a person works
 * through when sales-order pallets leave for an outside processor and come back (ship / pickup
 * next). API calls never throw ({ok, status, body, message}, utils/beacons beaconFetch).
 */
import { beaconFetch, currentActor, locationPlace } from '../../utils/beacons';

export function loadOpoChecklist(opoId, step) {
  return beaconFetch(`/outside_processing_orders/${opoId}/pallet_checklist?step=${encodeURIComponent(step)}`);
}

export function opoChecklistLights(opoId, step, action, palletIds = null) {
  return beaconFetch(`/outside_processing_orders/${opoId}/pallet_beacons/lights`, {
    method: 'POST',
    body: JSON.stringify({ step, action, pallet_ids: palletIds, actor: currentActor() }),
  });
}

export function recordOpoChecklist(opoId, step, { pin, items }) {
  return beaconFetch(`/outside_processing_orders/${opoId}/pallet_checklist`, {
    method: 'POST',
    body: JSON.stringify({ step, pin, items }),
  });
}

/**
 * The Send re-check (jobq-api 2026-10-29): does a Draft coating paper still carry what the packing plan packs?
 * {fix: false} answers {differs, lines, missing, paper, fixed, fixable, message} without changing anything;
 * {fix: true, expect} rewrites the Draft to that answer (`applied`); `expect` = the `fixed` totals shown (anything
 * else: 409 paper_changed with the new answer). `pin` names who fixed it (identity only).
 */
export function recheckOpo(opoId, { fix = false, pin = null, expect = null } = {}) {
  return beaconFetch(`/coating/manifests/${opoId}/recheck`, {
    method: 'POST',
    body: JSON.stringify({ fix, ...(pin ? { pin } : {}), ...(expect ? { expect } : {}) }),
  });
}

// The packing plan (2026-10-26): load-out (ship | pickup) and the package guide's Find all (find).
export function loadPlanChecklist(planId, step) {
  return beaconFetch(`/packaging/plans/${planId}/pallet_checklist?step=${encodeURIComponent(step)}`);
}

export function planChecklistLights(planId, step, action, palletIds = null) {
  return beaconFetch(`/packaging/plans/${planId}/pallet_beacons/lights`, {
    method: 'POST',
    body: JSON.stringify({ step, action, pallet_ids: palletIds, actor: currentActor() }),
  });
}

// Any list of sales-order pallets on any plans (2026-10-27): the Outsourcing staging board's Find / Find all.
export function loadPalletFind(palletIds) {
  return beaconFetch(`/packaging/pallet_find?pallet_ids=${encodeURIComponent((palletIds || []).join(','))}`);
}

export function palletFindLights(palletIds, action) {
  return beaconFetch('/packaging/pallet_find/lights', {
    method: 'POST',
    body: JSON.stringify({ pallet_ids: palletIds, action, actor: currentActor() }),
  });
}

/** Pallets a from_vendor list still asks about: back, with a beacon, not put back yet. */
export function reattachPending(checklist) {
  const rows = (checklist && checklist.pallets) || [];
  return rows.filter((r) => r.back && r.beacon_id && !r.done);
}

const METHOD_WORD = { scan: 'scanned', typed: 'typed', tick: 'checked' };
export const methodWord = (m) => METHOD_WORD[m] || m || '';

/** Where the pallet's beacon is, in a few words ('' when unknown). */
export function beaconWhereText(row) {
  if (!row || !row.beacon_id) return '';
  if (row.beacon_docked) return `docked at ${row.beacon_docked_at || 'a dock'}`;
  const place = locationPlace(row.location);
  return place ? `last seen: ${place}` : '';
}

/** The line under a pallet: what to do with it for this step. */
const STATUS_WORD = { Building: 'still being packed', Ready: 'ready to ship', Staged: 'staged for pickup' };

export function rowTodo(row, step) {
  if (!row) return '';
  if ((step === 'find' || step === 'ship' || step === 'pickup') && row.beacon_away) {
    // its beacon came off when the pallet went to a vendor: it sits on the shipping shelf, so lighting it finds
    // the shelf, not the pallet
    return `${row.beacon_id} is on the shipping shelf (the pallet went to the vendor): find the pallet by its label.`;
  }
  if (step === 'find') {
    const word = STATUS_WORD[row.status] || (row.status || '').toLowerCase();
    return row.beacon_id ? `${row.beacon_id} · ${word}.` : `No beacon: find it by its label · ${word}.`;
  }
  if (step === 'ship' || step === 'pickup') {
    const where = step === 'ship' ? 'it goes on the truck' : 'the customer loads it';
    return row.beacon_id
      ? `Pull ${row.beacon_id} (it stays here), then check the pallet off as ${where}.`
      : `No beacon. Check the pallet off as ${where}.`;
  }
  if (step === 'from_vendor') {
    if (!row.back) return 'Pieces still at the vendor.';
    return `Put ${row.beacon_id} back on ${row.short_id}${row.beacon_docked ? ` (it is ${beaconWhereText(row)})` : ''}.`;
  }
  if (row.beacon_id) {
    return row.beacon_docked
      ? `${row.beacon_id} is off the pallet (${beaconWhereText(row)}). Check the 4x6 label is on.`
      : `Pull ${row.beacon_id}, dock it on the shipping shelf, check the 4x6 label is on.`;
  }
  return 'No beacon. Check the 4x6 label is on.';
}
